import OrderExportDialog from '../components/OrderExportDialog';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import PageHeader from '../components/PageHeader';
import SearchBar from '../components/SearchBar';
import IconButton from '../components/IconButton';
import DistributorOrderForm from '../components/distributor/DistributorOrderForm';
import distributorService from '../services/distributorService';
import NewOrderModal from '../components/NewOrderModal';
import DeleteRecordModal from '../components/DeleteRecordModal';
import orderService from '../services/orderService';
import reportService from '../services/reportService';
import clinicService from '../services/clinicService';
import { useAuth } from '../context/AuthContext';
import { consumeQuickAction } from '../utils/quickAction';
import { isAdmin } from '../utils/roles';
import DateRangeFilter from '../components/DateRangeFilter';
import useDebounced from '../hooks/useDebounced';
import { useT, useLocale } from '../i18n/LanguageContext';
import { requestErrorMessage } from '../utils/requestError';
import useVisibleRefresh from '../hooks/useVisibleRefresh';
import { captureSession, isSessionCurrent } from '../utils/authSession';
import OrderDetailsModal from '../features/orders/OrderDetailsModal';
import useAccessibleTabs from '../hooks/useAccessibleTabs';
import AdminBusinessContextFilter from '../components/AdminBusinessContextFilter';
import OrderListTable from '../features/orders/OrderListTable';

// Completed sits after Shipped because that is the order an order moves through them: a delivered
// shipment moves the order from shipped to completed (shipments.service). Without the tab, every
// finished order was reachable from All only, and a rep whose orders had all been delivered saw
// "No orders found" under every other tab.
// New orders are created Approved on the shared API. Historical Submitted rows remain in All.
const directTabs = ['All', 'Approved', 'Shipped', 'Completed', 'Cancelled'];
const TAB_STATUS = {
  Approved: 'approved',
  Shipped: 'shipped',
  Completed: 'completed',
  Cancelled: 'cancelled',
  Processing: 'submitted',
};
// Server page size, sent explicitly so the window the pager describes is the one the
// server actually cut.
const PAGE_SIZE = 20;
/* \u2500\u2500\u2500 Main Page \u2500\u2500\u2500 */
export default function OrderManagement() {
  const t = useT();
  const locale = useLocale();
  const { user, sessionVersion } = useAuth();
  const exportFlight = useRef(null);
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const admin = isAdmin(user);
  const [businessContext, setBusinessContext] = useState('all');
  const [distributorId, setDistributorId] = useState('');
  const [distributors, setDistributors] = useState([]);
  // A clinic-site login may have ordering disabled by its sales representative. In that case this
  // page is read-only for the clinic and the representative places its orders.
  const clinicLogin = user?.role === 'clinic';
  const distributorClinicLogin = clinicLogin && Boolean(user?.distributor);
  const tabs =
    distributorClinicLogin || (admin && businessContext === 'distributor')
      ? ['All', 'Processing', ...directTabs.slice(1)]
      : directTabs;
  const [activeTab, setActiveTab] = useState('All');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showNewOrder, setShowNewOrder] = useState(false);
  const clinicOrderForm = useRef(null);
  const focusClinicOrder = useCallback(() => {
    clinicOrderForm.current?.scrollIntoView?.({ block: 'start' });
    clinicOrderForm.current?.focus({ preventScroll: true });
  }, []);
  // The order an admin has asked to delete, pending confirmation.
  const [deleteTarget, setDeleteTarget] = useState(null);
  // true only when the clinic's own row says orderingEnabled === false. Defaults to "on" and
  // stays "on" when the row cannot be loaded - the page then behaves exactly as before, and the
  // server still enforces the switch on submit.
  const [orderingOff, setOrderingOff] = useState(distributorClinicLogin);
  // Reps and admins know at once; a clinic login knows once its row has been read (or failed),
  // so a read-only clinic never sees the New Order button flash before the answer arrives.
  const [clinicKnown, setClinicKnown] = useState(!clinicLogin);
  useEffect(() => {
    if (distributorClinicLogin && showNewOrder && !orderingOff) focusClinicOrder();
  }, [distributorClinicLogin, showNewOrder, orderingOff, focusClinicOrder]);
  // A Dashboard "New Order" quick action from a clinic login waits for the clinic row: it is
  // consumed on mount (so it can never fire twice or survive a refresh) and honoured only once
  // the switch is known to be on.
  const pendingQuickAction = useRef(false);
  // Mount effect, not a useState initializer - see the StrictMode note in IVRDetails.js.
  useEffect(() => {
    if (!consumeQuickAction('new_order')) return;
    if (clinicLogin) pendingQuickAction.current = true;
    else setShowNewOrder(true);
  }, [clinicLogin, distributorClinicLogin]);

  // Clinic logins only: one request, on mount. GET /clinics returns the login's own clinic as its
  // single row, now carrying orderingEnabled. Reps and admins never make this request.
  const ownClinicId = user?.clinic?._id || user?.clinic || '';
  useEffect(() => {
    if (!clinicLogin) return undefined;
    let cancelled = false;
    const releaseQuickAction = (off) => {
      if (!pendingQuickAction.current) return;
      pendingQuickAction.current = false;
      if (!off) setShowNewOrder(true);
    };
    const session = captureSession();
    Promise.resolve(
      distributorClinicLogin
        ? distributorService.orderOptions(ownClinicId)
        : clinicService.getClinics()
    )
      .then((res) => {
        if (cancelled || !isSessionCurrent(session)) return;
        const data = res.data?.data?.data || res.data?.data || [];
        const rows = Array.isArray(data) ? data : [];
        const row = rows.find((c) => c && c._id === ownClinicId) || rows[0];
        const off = distributorClinicLogin
          ? !(
              res.data?.data?.orderingEnabled === true && res.data?.data?.submissionEnabled === true
            )
          : row?.orderingEnabled === false;
        setOrderingOff(off);
        setClinicKnown(true);
        releaseQuickAction(off);
      })
      .catch(() => {
        if (cancelled || !isSessionCurrent(session)) return;
        // Exactly as today: button shown, quick action honoured, server enforces.
        setOrderingOff(distributorClinicLogin);
        setClinicKnown(true);
        releaseQuickAction(distributorClinicLogin);
      });
    return () => {
      cancelled = true;
    };
  }, [clinicLogin, distributorClinicLogin, ownClinicId]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  // A failed list fetch used to be console.error'd and the page said "No orders found" - which is
  // a fact about the rep's orders, not about the request. The banner says which it was, and the
  // only a background refresh retains rows, with an explicit stale-status warning.
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  // One request per pause in typing. Clearing after creation must take effect immediately so
  // an old search cannot hide the newly approved order during the debounce window.
  const settledSearch = useDebounced(search, 300);
  const debouncedSearch = search ? settledSearch : '';
  // Date-submitted range. The filter button next to the search box used to have no onClick
  // at all - it looked live, invited a click and did nothing.
  const [dates, setDates] = useState({ dateFrom: '', dateTo: '' });
  // The list is server-paginated at PAGE_SIZE rows. The page used to discard the total the server
  // returns and show page 1 only, so the newest 20 were all anyone could ever reach.
  const [page, setPage] = useState(1);
  const [pageInfo, setPageInfo] = useState({ total: 0, totalPages: 1, limit: PAGE_SIZE });
  // Out-of-order responses: only the latest request may write the list.
  const seq = useRef(0);

  const fetchOrders = useCallback(
    async ({ background = false } = {}) => {
      const mine = ++seq.current;
      const session = captureSession();
      if (!background) {
        setLoading(true);
        setError('');
      }
      try {
        const params = { page, limit: PAGE_SIZE };
        if (admin && businessContext !== 'all') params.businessContext = businessContext;
        if (admin && businessContext === 'distributor' && distributorId)
          params.distributorId = distributorId;
        if (activeTab !== 'All') {
          params.status = TAB_STATUS[activeTab] || activeTab.toLowerCase();
        }
        if (debouncedSearch) params.search = debouncedSearch;
        if (dates.dateFrom) params.dateFrom = dates.dateFrom;
        if (dates.dateTo) params.dateTo = dates.dateTo;

        const ordersRes = await orderService.getOrders(params);
        if (mine !== seq.current || !isSessionCurrent(session)) return;

        const payload = ordersRes.data?.data || {};
        const data = payload.data || [];
        setOrders(Array.isArray(data) ? data : []);
        setError('');
        setPageInfo({
          total: payload.total || 0,
          totalPages: payload.totalPages || 1,
          limit: payload.limit || PAGE_SIZE,
        });
      } catch (err) {
        if (mine !== seq.current || !isSessionCurrent(session)) return;
        if (!background) setOrders([]);
        setError(
          background
            ? 'Could not refresh orders. Statuses may be out of date.'
            : requestErrorMessage(err, 'Could not load orders.')
        );
      } finally {
        if (mine === seq.current && isSessionCurrent(session)) setLoading(false);
      }
    },
    [activeTab, debouncedSearch, dates, page, admin, businessContext, distributorId]
  );

  useEffect(() => {
    fetchOrders();
    return () => {
      seq.current += 1;
    };
  }, [fetchOrders]);
  useVisibleRefresh(fetchOrders, !loading && !selectedOrder && !showNewOrder && !deleteTarget);

  // Clamp the page back into range when the list shrinks beneath it (an admin deleting the last
  // order on the last page, say). totalPages is always >= 1, so this lands on a real page, and the
  // page change triggers the refetch that fills it.
  useEffect(() => {
    if (page > pageInfo.totalPages) {
      setPage(pageInfo.totalPages);
    }
  }, [page, pageInfo.totalPages]);

  // Changing what is listed returns to page 1 - otherwise a filter with fewer pages than the
  // current page number shows an empty list. Tab and dates reset in their handlers (one render);
  // search resets when the DEBOUNCED value lands, which is when the list actually changes.
  const selectTab = (tab) => {
    setActiveTab(tab);
    setPage(1);
  };
  const { tabListProps, getTabProps, panelProps } = useAccessibleTabs({
    tabs,
    activeTab,
    onSelect: selectTab,
    label: t('Order views'),
  });
  const onDatesChange = (next) => {
    setDates(next);
    setPage(1);
  };
  const onContextChange = (context, organization) => {
    seq.current += 1;
    if (activeTab === 'Processing' && context !== 'distributor') setActiveTab('All');
    setBusinessContext(context);
    setDistributorId(organization);
    setOrders([]);
    setPageInfo({ total: 0, totalPages: 1, limit: PAGE_SIZE });
    setPage(1);
  };
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const onOrderCreated = (result) => {
    // Start at the newest Approved orders even when creation began on a different tab/page or
    // with older filters. Invalidate an in-flight read before its stale results can paint.
    seq.current += 1;
    setActiveTab(result?.status === 'submitted' ? 'Processing' : 'Approved');
    if (distributorClinicLogin) setShowNewOrder(false);
    setPage(1);
    setSearch('');
    if (admin) {
      setBusinessContext('direct');
      setDistributorId('');
    }
    // A fresh range object also triggers the refetch when already on Approved, page 1.
    setDates({ dateFrom: '', dateTo: '' });
  };

  const { total, totalPages, limit } = pageInfo;
  const firstRow = total === 0 ? 0 : (page - 1) * limit + 1;
  const lastRow = Math.min(page * limit, total);

  // Every role can export its own orders. The server tenant-scopes the result and leaves financial
  // columns blank for non-admin users.
  const [exportingOrders, setExportingOrders] = useState(false);
  const [exportDialog, setExportDialog] = useState(false);
  useEffect(() => {
    exportFlight.current = null;
    setExportDialog(false);
    setExportingOrders(false);
  }, [sessionVersion, user?._id]);
  const handleExportOrders = async (range) => {
    if (exportFlight.current) return false;
    const session = captureSession();
    const reservation = {};
    exportFlight.current = reservation;
    const current = () =>
      mounted.current && exportFlight.current === reservation && isSessionCurrent(session);
    setExportingOrders(true);
    try {
      const params = { ...range };
      if (admin && businessContext !== 'all') params.businessContext = businessContext;
      if (admin && businessContext === 'distributor' && distributorId)
        params.distributorId = distributorId;
      if (activeTab !== 'All') params.status = TAB_STATUS[activeTab] || activeTab.toLowerCase();
      if (debouncedSearch) params.search = debouncedSearch;
      const res = await reportService.exportMyOrders(params);
      if (!current()) return false;
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = range.dateFrom
        ? `orders-${range.dateFrom}-to-${range.dateTo}.xlsx`
        : `orders-export-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      return true;
    } catch (err) {
      // Export failures may include private backend diagnostics; use only authored guidance.
      if (current()) alert(t(requestErrorMessage(err, 'Failed to export orders')));
      return false;
    } finally {
      if (current()) setExportingOrders(false);
      if (exportFlight.current === reservation) exportFlight.current = null;
    }
  };

  return (
    <div>
      <PageHeader
        title={t('Order Management')}
        subtitle={
          admin
            ? t('Manage orders and track shipments for your practices.')
            : t('Create and track your orders.')
        }
      >
        <SearchBar
          placeholder={t('Search orders...')}
          className="w-[243px]"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <DateRangeFilter dateFrom={dates.dateFrom} dateTo={dates.dateTo} onChange={onDatesChange} />
        <IconButton
          icon={exportingOrders ? 'hourglass_empty' : 'download'}
          onClick={() => setExportDialog(true)}
          title={t('Export my orders (Excel)')}
        />
        {clinicKnown && !orderingOff && (
          <button
            onClick={() => {
              setShowNewOrder(true);
              focusClinicOrder();
            }}
            className="h-[46px] px-4 bg-[#0089ff] text-white text-sm font-semibold rounded-[10px] hover:bg-[#0077e6] transition-colors flex items-center gap-1.5"
          >
            <span className="material-symbols-outlined text-[18px]">add</span>
            {t('New Order')}
          </button>
        )}
      </PageHeader>
      {admin && (
        <AdminBusinessContextFilter
          {...{ businessContext, distributorId, t }}
          onChange={onContextChange}
          onDistributors={setDistributors}
          hasDistributorRecords={orders.some((order) => order.businessContext === 'distributor')}
        />
      )}

      {/* Read-only clinic (My Hub): calm and informational, not an error - nothing is wrong, the
          rep simply places this clinic's orders. */}
      {distributorClinicLogin ? (
        <div className="mb-4 px-4 py-3 rounded-[10px] text-[13px] font-medium bg-[#e6f1ff] text-[#0069c4]">
          {t(
            orderingOff
              ? 'Ordering is unavailable. Contact your distributor for assistance.'
              : 'Submit and track orders for your clinic here.'
          )}
        </div>
      ) : (
        orderingOff && (
          <div className="mb-4 px-4 py-3 rounded-[10px] text-[13px] font-medium bg-[#e6f1ff] text-[#0069c4]">
            {t(
              'Ordering is turned off for your clinic. Your sales representative places orders for you - you can still see every order here.'
            )}
          </div>
        )
      )}

      {distributorClinicLogin && showNewOrder && !orderingOff && (
        <div
          ref={clinicOrderForm}
          role="group"
          aria-label={t('New order')}
          tabIndex={-1}
          className="mb-6 scroll-mt-24 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#0089ff]"
        >
          <DistributorOrderForm
            key={`${sessionVersion}:${ownClinicId}`}
            clinicMode
            clinicId={ownClinicId}
            t={t}
            onSubmitted={onOrderCreated}
          />
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-2 mb-6" {...tabListProps}>
        {tabs.map((tab) => (
          <button
            key={tab}
            {...getTabProps(tab)}
            onClick={() => selectTab(tab)}
            className={`px-4 h-10 rounded-lg text-[13px] font-semibold transition-colors flex items-center gap-1.5 ${
              activeTab === tab
                ? 'bg-[#0f172a] text-white'
                : 'bg-[#f7f8fb] text-[#64748b] hover:bg-[#e2e8f0]'
            }`}
          >
            {t(tab)}
          </button>
        ))}
      </div>

      {error && (
        <div
          role="alert"
          className="mb-4 p-3 rounded-lg text-sm bg-red-50 border border-red-200 text-red-600"
        >
          {t(error)}
          <button
            type="button"
            className="ml-3 underline"
            disabled={loading}
            onClick={() => fetchOrders()}
          >
            {t('Retry')}
          </button>
        </div>
      )}

      {/* Failed foreground reads show only the banner; stale rows belong only to an explicitly
          labelled background refresh of the same view. */}
      <div {...panelProps}>
        {loading ? (
          <div role="status" aria-live="polite" className="flex items-center justify-center h-40">
            <span className="sr-only">{t('Loading orders...')}</span>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0089ff]" />
          </div>
        ) : error && orders.length === 0 ? null : (
          <div className="bg-white border border-[#e2e8f0] rounded-[14px] shadow-sm overflow-x-auto">
            <OrderListTable
              orders={orders}
              admin={admin}
              showSaleAmount={admin || distributorClinicLogin}
              distributors={distributors}
              t={t}
              locale={locale}
              onView={setSelectedOrder}
              onDelete={setDeleteTarget}
            />
            {/* Depth + reachability, exactly as on the IVR queue: "Showing X-Y of TOTAL" says how
              deep the list is, Prev/Next make all of it reachable. The pager only renders past one
              page, so a short list looks as it did. page <= totalPages guards the one-frame
              transient before the clamp effect fires. */}
            {total > 0 && page <= totalPages && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-[#e2e8f0] bg-[rgba(226,232,240,0.15)]">
                <span className="text-xs font-medium text-[#64748b]">
                  {t('Showing {first}–{last} of {total}', {
                    first: firstRow,
                    last: lastRow,
                    total,
                  })}
                </span>
                {totalPages > 1 && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1}
                      className="px-3 h-8 rounded-lg text-[12px] font-semibold bg-[#f7f8fb] text-[#0f172a] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#e2e8f0] transition-colors"
                    >
                      {t('Previous')}
                    </button>
                    <span className="text-xs font-medium text-[#64748b]">
                      {t('Page {page} of {total}', { page, total: totalPages })}
                    </span>
                    <button
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                      className="px-3 h-8 rounded-lg text-[12px] font-semibold bg-[#f7f8fb] text-[#0f172a] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#e2e8f0] transition-colors"
                    >
                      {t('Next')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {exportDialog && (
        <OrderExportDialog
          filtered
          initialDates={dates}
          onClose={() => setExportDialog(false)}
          onExport={handleExportOrders}
        />
      )}
      <OrderDetailsModal
        order={selectedOrder}
        onClose={() => setSelectedOrder(null)}
        onAction={fetchOrders}
        canAdminister={admin}
      />

      <NewOrderModal
        open={!distributorClinicLogin && showNewOrder && !orderingOff}
        onClose={() => setShowNewOrder(false)}
        onCreated={onOrderCreated}
      />

      <DeleteRecordModal
        open={!!deleteTarget}
        label={t('order {id}', { id: deleteTarget?.orderId || '' })}
        allowForce
        onCancel={() => setDeleteTarget(null)}
        onDelete={(force) => orderService.deleteOrder(deleteTarget._id, force)}
        onDone={() => {
          setDeleteTarget(null);
          fetchOrders();
        }}
      />
    </div>
  );
}
